/**
 * 配合服务端限速（#897）
 *
 * 网关（traefik）限速回的 429 是**纯文本、带 Retry-After**，不是后端 JSON 错误信封，
 * 所以拦截器原来那串 `err.response.data.error || … || "请求错误"` 取不到任何字段，只剩「请求错误」。
 * 这里集中放：Retry-After 解析、提示文案、业务接口 429 的处理、刷新令牌失败的分类。
 *
 * ⚠ 本文件在五个应用（approval / case / domain / insight / website）的 src/utils/ 下各一份、逐字节相同
 *   （五个应用各自独立构建，CRA 不允许 import src 以外的文件）。改一处须同步其余四处。
 */

/** Retry-After 缺省秒数（头缺失或认不出时） */
export const RETRY_AFTER_DEFAULT_SECONDS = 2;
/** Retry-After 上限秒数 */
export const RETRY_AFTER_MAX_SECONDS = 60;
/** 刷新令牌遇 429 / 网络错 / 5xx 时最多重试的次数 */
export const REFRESH_MAX_RETRIES = 3;
/**
 * 业务接口网关 429：Retry-After 不超过这个秒数时先静默重试一次，再 429 才提示。
 * 不分请求方法：网关 429 是在转发前给出的、请求没到后端，重试不会重复执行副作用（限速设计稿 v1.9）。
 * 后端自己回的 JSON 429（请求已到后端）不走这里、一律不重试（#898）。
 */
export const SILENT_RETRY_MAX_SECONDS = 1;

export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const rateLimitMessage = (seconds: number) => `请求太频繁，请 ${seconds} 秒后再试`;

/**
 * 解析 Retry-After（秒数或 HTTP 日期两种写法）⇒ 1 ~ 60 的整秒；缺失 / 认不出 ⇒ 2
 */
export const parseRetryAfter = (value: unknown): number => {
    let seconds = NaN;
    if (typeof value === "number") {
        seconds = value;
    } else if (typeof value === "string" && value.trim() !== "") {
        const text = value.trim();
        if (/^\d+(\.\d+)?$/.test(text)) {
            seconds = Number(text);
        } else {
            const at = Date.parse(text);
            if (!isNaN(at)) {
                seconds = (at - Date.now()) / 1000;
            }
        }
    }
    if (!isFinite(seconds)) {
        return RETRY_AFTER_DEFAULT_SECONDS;
    }
    return Math.min(RETRY_AFTER_MAX_SECONDS, Math.max(1, Math.ceil(seconds)));
};

/** 从 axios 响应里取 Retry-After 秒数 */
export const getRetryAfterSeconds = (response: any): number => {
    const headers = response?.headers;
    const value =
        typeof headers?.get === "function"
            ? headers.get("retry-after")
            : headers?.["retry-after"] ?? headers?.["Retry-After"];
    return parseRetryAfter(value);
};

/** 状态码是不是 429（不分网关 / 后端；要分用下面的 isGatewayRateLimited / isBackendRateLimited） */
export const isRateLimited = (err: any): boolean => err?.response?.status == 429;

/**
 * 两种 429 的判别（#898）。2026-10-20 实打预发 staging 读到的响应形状：
 * - 网关限速（traefik ratelimit）：`429 Too Many Requests`，**没有 Content-Type**，体是纯文本 `Too Many Requests`，
 *   带 `Retry-After`（另有 `X-Retry-In`）。请求没到后端。
 * - 后端业务 429（如 gsp-auth 发验证码按号码限频）：`Content-Type: application/json`，
 *   体 `{"error":"验证码发送太频繁, 请稍后再尝试","code":3}`，**也带** `Retry-After: 60`。
 * ⇒ 两种都带 Retry-After，不能按头判。判据 = 响应体是不是带业务 `code` 字段的 JSON 对象：
 *   是 ⇒ 后端业务 429（原样把后端的提示交给页面，不静默重试、不按 Retry-After 倒数——请求可能已在后端执行过）；
 *   否则 ⇒ 网关限速，照 #897 处理。
 */
export const getBackendErrorBody = (data: unknown): Record<string, any> | null => {
    let body: unknown = data;
    if (typeof body === "string") {
        try {
            body = JSON.parse(body);
        } catch (e) {
            return null;
        }
    }
    if (!body || typeof body !== "object" || Array.isArray(body)) {
        return null;
    }
    const code = (body as Record<string, any>).code;
    return code === undefined || code === null || code === "" ? null : (body as Record<string, any>);
};

/** 后端业务 429（JSON 体带业务 code） */
export const isBackendRateLimited = (err: any): boolean =>
    isRateLimited(err) && getBackendErrorBody(err?.response?.data) !== null;

/** 网关限速的 429（429 且体不是带业务 code 的 JSON） */
export const isGatewayRateLimited = (err: any): boolean => isRateLimited(err) && !isBackendRateLimited(err);

/**
 * 业务接口网关 429 的处理（请求实例的响应拦截器里调用；只给网关 429，后端 JSON 429 不进来，#898）：
 * - Retry-After ≤ 1 秒、且这个请求还没重试过 ⇒ 等 Retry-After 后静默重试一次（不分请求方法：请求没到后端）；
 * - 否则 ⇒ 以「请求太频繁，请 N 秒后再试」拒绝（与原来拒绝「请求错误」同一形状：字符串）。
 */
export const handleRateLimited = (retry: (config: any) => Promise<any>, err: any): Promise<any> => {
    const seconds = getRetryAfterSeconds(err?.response);
    const config = err?.config;
    if (config && !config.__rateLimitRetried && seconds <= SILENT_RETRY_MAX_SECONDS) {
        config.__rateLimitRetried = true;
        return sleep(seconds * 1000).then(() => retry(config));
    }
    return Promise.reject(rateLimitMessage(seconds));
};

/** 认证请求（刷新令牌等）的失败，保留状态码与 Retry-After 供分类 */
export interface AuthRequestError {
    status?: number;
    retryAfterSeconds?: number;
    message: string;
}

export const toAuthRequestError = (err: any, message: string): AuthRequestError => ({
    status: err?.response?.status,
    retryAfterSeconds: isRateLimited(err) ? getRetryAfterSeconds(err.response) : undefined,
    message,
});

export type RefreshFailure =
    | { kind: "invalid" } // 刷新令牌失效（401 / 其他 4xx / 认不出的失败）⇒ 清登录跳登录页
    | { kind: "rateLimited"; waitMs: number } // 429 ⇒ 按 Retry-After 等待后重试
    | { kind: "transient"; waitMs: number }; // 网络错 / 5xx ⇒ 退避重试

/**
 * 刷新令牌失败分三类。attempt 从 0 起，网络错 / 5xx 的退避 = 1s、2s、4s…
 * 认不出形状的失败（不是 toAuthRequestError 出来的）照旧按失效处理，不改原行为。
 */
export const classifyRefreshError = (error: any, attempt: number): RefreshFailure => {
    if (!error || typeof error !== "object" || !("status" in error)) {
        return { kind: "invalid" };
    }
    const status = error.status;
    if (status == 429) {
        return {
            kind: "rateLimited",
            waitMs: (error.retryAfterSeconds || RETRY_AFTER_DEFAULT_SECONDS) * 1000,
        };
    }
    if (status === undefined || status === null || status === 0 || status >= 500) {
        return { kind: "transient", waitMs: 1000 * Math.pow(2, attempt) };
    }
    return { kind: "invalid" };
};
